<template>
  <div class="container pb-10">
    <div class="flex items-center mb-10">
      <a href="/admin/forms/project-certification" class="text-gray-900 no-underline">
        <d-icon icon="arrow-left" size="1x" class="mr-3" />
        <span class="text-xl">Back To Listing Page</span>
      </a>
    </div>

    <div class="flex flex-wrap -mx-5">
      <div class="px-5 w-full md:w-1/2">
        <h2>Project Details</h2>
        <div>
          <div class="mb-3">
            <p class="font-bold">
              Project Number
            </p>
            <p v-text="computedProject.number" />
          </div>
          <div class="mb-3">
            <p class="font-bold">
              Project Name
            </p>
            <p v-text="computedProject.name" />
          </div>
          <div class="mb-3">
            <p class="font-bold">
              QCP Contract Amount
            </p>
            <p>
              ${{ submissionDetails.revisedContractAmount.toLocaleString("en-US", {style: "decimal", minimumFractionDigits: 2}) }}
            </p>
          </div>
          <div class="mb-3">
            <p class="font-bold">
              City
            </p>
            <p v-text="computedProject.city" />
          </div>
          <div class="mb-3">
            <p class="font-bold">
              State
            </p>
            <p v-text="computedProject.stateProvince" />
          </div>
          <div v-if="computedProject.estimatedCompletionDate" class="mb-3">
            <p class="font-bold">
              Estimated Completion Date
            </p>
            <p>{{ computedProject.estimatedCompletionDate | formatShortDate }}</p>
          </div>
        </div>
      </div>

      <div class="px-5 w-full md:w-1/2">
        <h2>Transaction Details</h2>
        <div>
          <div class="mb-3">
            <p class="font-bold">
              Amount
            </p>
            <p>
              ${{ computedTransactionDetails.amount.toLocaleString("en-US", {style: "decimal", minimumFractionDigits: 2}) }}
            </p>
          </div>
          <div v-if="computedTransactionDetails.companyName" class="mb-3">
            <p class="font-bold">
              Company Name
            </p>
            <p v-text="computedTransactionDetails.companyName" />
          </div>
          <div v-if="computedTransactionDetails.billingAddress1" class="mb-3">
            <p class="font-bold">
              Billing Address 1
            </p>
            <p v-text="computedTransactionDetails.billingAddress1" />
          </div>
          <div v-if="computedTransactionDetails.billingAddress2" class="mb-3">
            <p class="font-bold">
              Billing Address 2
            </p>
            <p v-text="computedTransactionDetails.billingAddress2" />
          </div>
          <div v-if="computedTransactionDetails.billingCity" class="mb-3">
            <p class="font-bold">
              Billing City
            </p>
            <p v-text="computedTransactionDetails.billingCity" />
          </div>
          <div v-if="computedTransactionDetails.billingStateProvince" class="mb-3">
            <p class="font-bold">
              Billing State
            </p>
            <p v-text="computedTransactionDetails.billingStateProvince" />
          </div>
          <div v-if="computedTransactionDetails.billingZip" class="mb-3">
            <p class="font-bold">
              Billing Zip
            </p>
            <p v-text="computedTransactionDetails.billingZip" />
          </div>
          <div v-if="computedTransactionDetails.billingEmail" class="mb-3">
            <p class="font-bold">
              Billing Email
            </p>
            <p v-text="computedTransactionDetails.billingEmail" />
          </div>
          <div v-if="computedTransactionDetails.billingPhone" class="mb-3">
            <p class="font-bold">
              Billing Phone
            </p>
            <p v-text="computedTransactionDetails.billingPhone" />
          </div>
        </div>
      </div>
    </div>

    <hr class="my-10">
    <h2>Woodworker Information</h2>
    <view-organization-details :organization.sync="computedSubmissionDetails.revisedWoodworkerCompany" :submission-source="SubmissionSource.OrderProjectCertification" @save-changes="$emit('save-changes', true)" />

    <hr class="my-16">

    <div class="flex flex-wrap -mx-5">
      <div class="px-5 w-full md:w-1/2">
        <h2>General Contractor Details</h2>
        <div>
          <div class="mb-3">
            <p class="font-bold">
              General Contractor Name
            </p>
            <p v-text="computedGCCompany.name" />
          </div>
          <div class="mb-3">
            <p class="font-bold">
              City
            </p>
            <p v-text="computedGCCompany.city" />
          </div>
          <div class="mb-3">
            <p class="font-bold">
              State/Province
            </p>
            <p v-text="computedGCCompany.state" />
          </div>
        </div>
      </div>

      <div class="px-5 w-full md:w-1/2">
        <h2>Design Firm Information</h2>
        <div>
          <div class="mb-3">
            <p class="font-bold">
              Firm Name
            </p>
            <p v-text="computedFirmCompany.name" />
          </div>
          <div class="mb-3">
            <p class="font-bold">
              Contact First Name
            </p>
            <p v-text="computedFirmCompany.name" />
          </div>
          <div class="mb-3">
            <p class="font-bold">
              Contact Last Name
            </p>
            <p v-text="computedFirmContact.firstName" />
          </div>
          <div class="mb-3">
            <p class="font-bold">
              Contact Email
            </p>
            <p v-text="computedFirmContact.lastName" />
          </div>
          <div class="mb-3">
            <p class="font-bold">
              City
            </p>
            <p v-text="computedFirmCompany.city" />
          </div>
          <div class="mb-3">
            <p class="font-bold">
              State
            </p>
            <p v-text="computedFirmCompany.state" />
          </div>
        </div>
      </div>
    </div>

    <hr class="my-16">

    <div class="flex flex-wrap -mx-5">
      <div class="px-5 w-full md:w-1/2">
        <h2>Label and/or Certificate Quantity</h2>
        <div>
          <div v-for="group in computedCertInfo" :key="group.name" class="mb-5">
            <p class="font-bold">
              {{ group.name }}
            </p>
            <ul v-for="item in group.items" :key="item.key">
              <li v-if="item.quantity > 0">
                <strong>{{ item.quantity }}</strong> - {{ item.name }} <span v-if="item.key">(#{{ item.key }})</key>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="px-5 w-full md:w-1/2">
        <h2>Current Project Status</h2>
        <div>
          <div class="mb-3">
            <p class="font-bold">
              Project Status
            </p>
            <p v-text="submissionDetails.revisedProjectStatus" />
          </div>
          <div class="mb-3">
            <p class="font-bold">
              Woodwork Mfg.
            </p>
            <p v-text="submissionDetails.revisedWoodworkManufacturing" />
          </div>
          <div class="mb-3">
            <p class="font-bold">
              Cabinet Mfg.
            </p>
            <p v-text="submissionDetails.revisedCabinetManufacturing" />
          </div>
          <div class="mb-3">
            <p class="font-bold">
              Door Supplier
            </p>
            <p v-text="submissionDetails.revisedDoorSupplier" />
          </div>
          <div class="mb-3">
            <p class="font-bold">
              Finisher
            </p>
            <p v-text="submissionDetails.revisedFinisher" />
          </div>
          <div class="mb-3">
            <p class="font-bold">
              Installed By
            </p>
            <p v-text="submissionDetails.revisedInstalledBy" />
          </div>
        </div>
      </div>
    </div>

    <hr class="my-16">

    <div class="flex flex-wrap -mx-5">
      <div class="px-5 w-full md:w-1/2">
        <h2>Certification Statement</h2>
        <div>
          <div class="mb-3">
            <p class="font-bold">
              Signature
            </p>
            <p v-text="submissionDetails.revisedSignature" />
          </div>
          <div class="mb-3">
            <p class="font-bold">
              Title
            </p>
            <p v-text="submissionDetails.revisedTitle" />
          </div>
          <div class="mb-3">
            <p class="font-bold">
              Email
            </p>
            <p v-text="submissionDetails.revisedEmail" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { SubmissionSource } from '@resources/scripts/models/enums';
  import ViewOrganizationDetails from '@resources/scripts/patterns/formPatterns/ViewOrganizationDetails.vue';

  export default {
    components: {
      ViewOrganizationDetails,
    },

    props: {
      submissionDetails: {
        type: Object,
        default: () => ({}),
      },
      transactionDetails: {
        type: Object,
        default: () => ({}),
      },
    },

    data() {
      return {
        SubmissionSource, // enum
      };
    },

    computed: {
      computedTransactionDetails: {
        get() {
          return this.transactionDetails;
        },
        set(value) {
          this.$emit('update:transactionDetails', value);
        },
      },

      computedSubmissionDetails: {
        get() {
          return this.submissionDetails;
        },
        set(value) {
          this.$emit('update:submissionDetails', value);
        },
      },

      computedProject() {
        return this.submissionDetails.revisedProject;
      },

      computedWWCompany() {
        return this.submissionDetails.revisedWoodworkerCompany;
      },

      computedWWContact() {
        return this.submissionDetails.revisedWoodworkerContact;
      },

      computedGCCompany() {
        return this.submissionDetails.revisedGeneralContractorCompany;
      },

      computedFirmCompany() {
        return this.submissionDetails.revisedDesignCompany;
      },

      computedFirmContact() {
        return this.submissionDetails.revisedDesignContact;
      },

      computedCertInfo() {
        return JSON.parse(this.submissionDetails.revisedCertificationInfo);
      },
    },
  };
</script>